import React, { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

const API = (path) =>
  fetch(path, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function OwnerAuditLog() {
  const [logs, setLogs]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [expanded, setExpanded] = useState(null);

  // Filters
  const [action, setAction]     = useState('');
  const [from, setFrom]         = useState('');
  const [to, setTo]             = useState('');

  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page, size: PAGE_SIZE });
    if (action) params.set('action', action);
    if (from)   params.set('from', from);
    if (to)     params.set('to', to);
    const res = await API(`/api/owner/audit-log?${params}`);
    const d = await res.json();
    if (!res.ok) { setError(d.error || 'Failed'); setLoading(false); return; }
    setLogs(d.logs || []);
    setTotal(d.total || 0);
    setLoading(false);
  }, [page, action, from, to]);

  useEffect(() => { load(); }, [load]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const actionColor = (a) => {
    if (!a) return '#e5e7eb';
    if (a.includes('delete') || a.includes('fail')) return '#fee2e2';
    if (a.includes('create') || a.includes('onboard')) return '#d1fae5';
    if (a.includes('waiv') || a.includes('override')) return '#ede9fe';
    if (a.includes('update') || a.includes('patch')) return '#dbeafe';
    return '#f3f4f6';
  };

  const actionTextColor = (a) => {
    if (!a) return '#6b7280';
    if (a.includes('delete') || a.includes('fail')) return '#991b1b';
    if (a.includes('create') || a.includes('onboard')) return '#065f46';
    if (a.includes('waiv') || a.includes('override')) return '#5b21b6';
    if (a.includes('update') || a.includes('patch')) return '#1e40af';
    return '#374151';
  };

  const exportCSV = () => {
    const headers = ['Timestamp', 'Actor', 'Email', 'Role', 'Action', 'Resource', 'Resource ID', 'Company', 'IP'];
    const rows = logs.map(l => [
      l.created_at, l.actor_name, l.actor_email, l.actor_role, l.action,
      l.resource, l.resource_id, l.company_name, l.ip_address
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-main">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '4px' }}>Audit Log</h1>
            <p style={{ color: '#6b7280', margin: 0 }}>Full platform activity log — FCA compliance record</p>
          </div>
          <button onClick={exportCSV} style={{ background: '#1e40af', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', cursor: 'pointer', fontWeight: 500 }}>
            ↓ Export CSV
          </button>
        </div>

        {error && <div style={alertRed}>{error}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap', background: '#fff', padding: '16px', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          <div>
            <label style={labelStyle}>Action keyword</label>
            <input
              placeholder="e.g. create, delete, waive…"
              value={action}
              onChange={e => { setAction(e.target.value); setPage(1); }}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>From date</label>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>To date</label>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} style={inputStyle} />
          </div>
          {(action || from || to) && (
            <div style={{ display: 'flex', alignItems: 'flex-end' }}>
              <button onClick={() => { setAction(''); setFrom(''); setTo(''); setPage(1); }} style={{ padding: '8px 14px', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '13px', color: '#6b7280' }}>
                Clear filters
              </button>
            </div>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'flex-end' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>{total.toLocaleString()} total entries</span>
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
          ) : logs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#9ca3af' }}>No log entries found</div>
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {['Timestamp', 'Actor', 'Role', 'Action', 'Resource', 'Company', 'IP', ''].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {logs.map(l => {
                    const isOpen = expanded === l.id;
                    return (
                      <React.Fragment key={l.id}>
                        <tr style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ ...td, fontFamily: 'monospace', fontSize: '12px', whiteSpace: 'nowrap', color: '#9ca3af' }}>
                            {l.created_at ? new Date(l.created_at).toLocaleString('en-GB') : '—'}
                          </td>
                          <td style={td}>
                            <div style={{ fontWeight: 500, fontSize: '13px' }}>{l.actor_name || '—'}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{l.actor_email}</div>
                          </td>
                          <td style={td}>
                            <span style={{ fontSize: '11px', background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: '4px', fontWeight: 500 }}>
                              {l.actor_role || '—'}
                            </span>
                          </td>
                          <td style={td}>
                            <span style={{ background: actionColor(l.action), color: actionTextColor(l.action), padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 500, fontFamily: 'monospace' }}>
                              {l.action}
                            </span>
                          </td>
                          <td style={td}>
                            <div style={{ fontSize: '13px' }}>{l.resource}</div>
                            {l.resource_id && <div style={{ fontSize: '11px', color: '#9ca3af', fontFamily: 'monospace' }}>{l.resource_id.slice(0, 8)}…</div>}
                          </td>
                          <td style={{ ...td, fontSize: '13px' }}>{l.company_name || '—'}</td>
                          <td style={{ ...td, fontSize: '12px', color: '#9ca3af', fontFamily: 'monospace' }}>{l.ip_address || '—'}</td>
                          <td style={td}>
                            {l.detail && (
                              <button onClick={() => setExpanded(isOpen ? null : l.id)} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', color: '#6b7280' }}>
                                {isOpen ? 'Hide' : 'Detail'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {isOpen && l.detail && (
                          <tr style={{ background: '#f9fafb' }}>
                            <td colSpan={8} style={{ padding: '12px 20px' }}>
                              <pre style={{ margin: 0, fontSize: '12px', color: '#374151', overflow: 'auto', maxHeight: '200px', background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #e5e7eb' }}>
                                {JSON.stringify(l.detail, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderTop: '1px solid #f3f4f6' }}>
                <span style={{ fontSize: '13px', color: '#6b7280' }}>
                  Page {page} of {totalPages} ({total.toLocaleString()} entries)
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => setPage(1)} disabled={page === 1} style={pageBtn(page === 1)}>«</button>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtn(page === 1)}>‹</button>
                  {[...Array(Math.min(5, totalPages))].map((_, i) => {
                    const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i;
                    return <button key={p} onClick={() => setPage(p)} style={pageBtn(false, p === page)}>{p}</button>;
                  })}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtn(page === totalPages)}>›</button>
                  <button onClick={() => setPage(totalPages)} disabled={page === totalPages} style={pageBtn(page === totalPages)}>»</button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

const inputStyle = { padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' };
const labelStyle = { display: 'block', fontSize: '12px', fontWeight: 500, color: '#6b7280', marginBottom: '4px' };
const th = { padding: '10px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '10px 16px', fontSize: '13px', color: '#374151', verticalAlign: 'middle' };
const alertRed = { background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' };
const pageBtn = (disabled, active = false) => ({
  padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: '6px',
  background: active ? '#1e40af' : '#fff', color: active ? '#fff' : disabled ? '#d1d5db' : '#374151',
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: '13px', minWidth: '36px',
});
